import L from 'leaflet';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import * as quadkeys from 'mt-quadkeys';
import queryString from 'query-string';
import moment from 'moment';

const GRIDAPI_DATE_FORMAT = 'YYYY-MM-DD HH:mm';

L.Control.GridApiError = L.Control.extend({
	options: {
		position: 'topleft',
	},
	initialize: function (options) {
		L.Control.prototype.initialize.call(this, options);
	},
	onAdd: function (map) {
		let container = L.DomUtil.create('div', 'layer-gridapi-error');

		container.innerHTML = this.options.message;

		return container;
	},
	onRemove: function (map) { },
});

function filterTimestamps(type) {
	let values = Array.prototype.slice.call(arguments, 1)
		.filter(item => typeof item === 'number');

	return Math[type].apply(null, values);
}

L.gridLayer.gridApi = function (options) {
	return new L.GridLayer.GridApi(options);
};

L.GridLayer.GridApi = L.GridLayer.extend({
	options: {
		errors: {
			noTiles: 'The server sent an empty list of tiles.',
			connectionError: 'There was an error with the connection to the server.',
		},

		cacheMaxImages: 2000,
		scaleTilesZoomGeq: null,
		hideBeforeZoom: null,
		hideAfterZoom: null,

		preloading: false,
		baseUrl: null,
		wmtsName: null,
		start: null,
		end: null,
		init: null,
	},

	initialize: function (options) {
		L.Util.setOptions(this, options);

		this._timeline = {
			startTime: null,
			stopTime: null,
			currentTime: Date.now(),
			currentRange: 0,
			currentStep: 0,
			ranges: [],
		};

		this._schemes = {
			current: null,
			preload: [],
			all: [],
		};

		this._images = {
			loading: {},
			loaded: {},
			failed: {},
		};
	},

	onAdd: function (map) {
		L.GridLayer.prototype.onAdd.call(this, map);

		map.on('zoomend', this._preloadSchemes, this);
		map.on('moveend', this._preloadSchemes, this);

		this._fetchData();
	},

	onRemove: function (map)  {
		if (this._error) {
			this._error.remove();
			delete this._error;
		}

		map.off('zoomend', this._preloadSchemes, this);
		map.off('moveend', this._preloadSchemes, this);

		L.GridLayer.prototype.onRemove.call(this, map);
	},

	getTimeline: function () {
		return cloneDeep(this._timeline);
	},

	_fetchData: function () {
		if (!this.options.baseUrl) {
			throw new Error('Argument "baseUrl" is missing.');
		}

		if (!this.options.wmtsName || this.options.wmtsName.length === 0) {
			throw new Error('Argument "wmtsName" is missing.');
		}

		let params = {};

		if (moment.isMoment(this.options.start)) {
			params.start = this.options.start.format(GRIDAPI_DATE_FORMAT);
		}

		if (moment.isMoment(this.options.end)) {
			params.end = this.options.end.format(GRIDAPI_DATE_FORMAT);
		}

		if (moment.isMoment(this.options.init)) {
			params.init = this.options.init.format(GRIDAPI_DATE_FORMAT);
		}

		let uri = this.options.baseUrl + 'wmts/' + this.options.wmtsName + '/list_tile_urls';
		uri += '?' + queryString.stringify(params);

		fetch(uri)
			.then(response => {
				if (response.ok) {
				    this.fire('LAYER_META_FETCHED', null, true);

					return response.json();
				} else {
					throw new Error(response.statusText || response.status);
				}
			})
			.then(this._extractData.bind(this))
			.catch(reason => {
				this._error = new L.Control.GridApiError({
					message: this.options.errors.connectionError,
				}).addTo(this._map);
			});
	},

	_extractData: function (json) {
		if (json.tiles.length === 0) {
			this._error = new L.Control.GridApiError({
				message: this.options.errors.noTiles,
			}).addTo(this._map);
		} else if (this._error) {
			this._error.remove();
			delete this._error;
		}

		let range_measurements = {
			label: 'Measurements',
			className: 'gridapi-range',
			startTime: null,
			stopTime: null,
			steps: [],
		};

		let range_forecasts = {
			label: 'Forecasts',
			className: 'gridapi-range',
			startTime: null,
			stopTime: null,
			steps: [],
		};

		let timeline = {
			label: 'GridAPI',
			className: 'gridapi-timeline',
			startTime: null,
			stopTime: null,
			ranges: [range_measurements, range_forecasts],
		};

		let schemes = [[], []];

		json.tiles.forEach(tile => {
			let timestamp = moment.utc(tile.time, 'YYYYMMDDHHmm').valueOf();
			let index = tile.forecast ? 1 : 0;

			let step = {
				label: '',
				className: 'gridapi-step',
				dataTime: timestamp,
			};

			timeline.ranges[index].startTime = filterTimestamps('min', timestamp, timeline.ranges[index].startTime);
			timeline.ranges[index].stopTime = filterTimestamps('max', timestamp, timeline.ranges[index].stopTime);
			timeline.ranges[index].steps.push(step);

			schemes[index].push(tile.url);
		});

		timeline.startTime = range_measurements.startTime || range_forecasts.startTime;
		timeline.stopTime = range_forecasts.stopTime || range_measurements.stopTime;

		this._images = {
			loading: {},
			loaded: {},
			failed: {},
		};

		Object.assign(this._timeline, timeline);
		this._schemes.all = schemes;

		this.fire('LAYER_META_EXTRACTED', null, true);

		this.setTime(this._timeline.currentTime);
	},

	setTime: function (timestamp) {
		if (this._container) {
			this._container.style.display = 'none';
		}

		if (typeof timestamp !== 'number') {
			return;
		}

		this._timeline.currentTime = timestamp;

		if (!this._map || this._timeline.ranges.length === 0) {
			return;
		}

		if (this._timeline.startTime <= timestamp && timestamp <= this._timeline.stopTime) {
			this._timeline.ranges.forEach((range, range_index) => {
				if (range.startTime <= timestamp && timestamp <= range.stopTime) {
					let step_index;

					for (let i = range.steps.length - 1; i >= 0; i--) {
						if (timestamp >= range.steps[i].dataTime) {
							step_index = i;
							break;
						}
					}

					this._setIndices(range_index, step_index);
				}
			});
		}
	},

	_setIndices: function (range, step) {
		if (range === undefined || step === undefined) {
			return;
		}

		this._timeline.currentRange = range;
		this._timeline.currentStep = step;

		this._schemes.current = this._schemes.all[this._timeline.currentRange][this._timeline.currentStep];

		if (step + 1 < this._schemes.all[range].length) {
			this._schemes.next = this._schemes.all[range][step + 1];
		} else if (range + 1 < this._schemes.all.length) {
			this._schemes.next = this._schemes.all[range + 1][0];
		} else {
			this._schemes.next = this._schemes.all[0][0];
		}

		if (this.options.preloading) {
			this._schemes.preload = flatten(this._schemes.all);
		} else {
			this._schemes.preload = [this._schemes.current];

			if (this._schemes.next) {
				this._schemes.preload.push(this._schemes.next);
			}
		}

		this._preloadSchemes();

		Object.keys(this._tiles).forEach(key => {
			let tile = this._tiles[key];
			this.drawContent(tile.el, tile.coords);
		});

		this._container.style.display = 'block';
	},

	_preloadSchemes: function () {
		// make sure number of memory cached images is not unbounded
		var cache_length = Object.keys(this._images.loaded).length;

		if (cache_length > this.options.cacheMaxImages) {
			this._images.loaded = {};
		}

		if (!this._schemes.preload) {
			return;
		}

		// compute total number of tiles to be loaded
		var total = Object.keys(this._tiles || {}).length;

		if (total == 0) {
			// layer has no tiles, i.e., it's not active
			return;
		}
		total *= this._schemes.preload.length;

		var loaded = 0;

		var callback = function (image, cached) {
			loaded++;

			if (!image || cached) {
				return;
			}

			this.fire('LAYER_TILE_LOADED', { cached, loaded, total });
		}.bind(this);

		this._schemes.preload.forEach(scheme => {
			this._loadScheme(scheme, callback);
		});
	},

	_loadScheme: function (scheme, callback) {
		Object.keys(this._tiles).forEach(key => {
			let tile = this._tiles[key];
			let coords = tile.coords;
			let zoom = coords.z;

			if (this.options.hideBeforeZoom !== null && zoom < this.options.hideBeforeZoom) {
				return;
			}

			if (this.options.hideAfterZoom !== null && zoom > this.options.hideAfterZoom) {
				return;
			}

			var quadkey = quadkeys.tile2key(coords.x, coords.y, coords.z);

			// check if client side scaling is active for this zoom level...
			if (this.options.scaleTilesZoomGeq !== null && zoom >= this.options.scaleTilesZoomGeq) {
				// preload tiles for level this.options.scaleTilesZoomGeq - 1
				// how many zoom levels away from this.options.scaleTilesZoomGeq - 1?
				var no_zlevels = zoom - this.options.scaleTilesZoomGeq + 1;
				// strip as many digits from quadkey
				quadkey = quadkey.slice(0, -no_zlevels);
			}

			this._loadTile(quadkey, scheme, callback, callback);
		});
	},

	_loadTile: function (quadkey, urlscheme, resolve, reject) {
		if (typeof resolve === 'undefined') {
			resolve = null;
		}

		if (typeof reject === 'undefined') {
			reject = null;
		}

		// string to be used as key in this._images.loaded.
		// Note that we don't use the url because the protocol part may be missing
		// in the request URL (protocol-relative URLs) or may change due to http
		// redirection.
		var cache_key = quadkey + '_' + urlscheme;

		if (cache_key in this._images.loaded) {
			// image is already loaded
			var image = this._images.loaded[cache_key];

			if (resolve !== null) {
				resolve(image, true);
			}
		} else if (cache_key in this._images.failed) {
			// Do not try to load tiles that have failed previously.
			if (reject !== null) {
				reject(null, true);
			}
		} else if (cache_key in this._images.loading) {
			// image is currently being loaded
			if (resolve !== null) {
				var image = this._images.loading[cache_key];

				image.addEventListener('load', event => {
					resolve(image, false);
				});
			}

			if (reject !== null) {
				var image = this._images.loading[cache_key];

				image.addEventListener('error', event => {
					reject(image, false);
				});
			}
		} else {
			var image = new Image();

			image.addEventListener('load', () => {
				this._images.loaded[cache_key] = image;
				delete this._images.loading[cache_key];

				if (resolve !== null) {
					resolve(image, false);
				}
			});

			image.addEventListener('error', () => {
				this._images.failed[cache_key] = true;
				delete this._images.loading[cache_key];

				if (reject !== null) {
					reject(image, false);
				}
			});

			var coords = quadkeys.key2tile(quadkey);
			var url = L.Util.template(urlscheme, coords);

			this._images.loading[cache_key] = image;

			image.src = url;
		}
	},

	/**
	 * overrides/implements L.GridLayer.createTile()
	 */
	createTile: function (coords) {
		// create canvas element, same size as tiles
		var size = this.getTileSize();

		var canvas = L.DomUtil.create('canvas', 'leaflet-tile');
		canvas.width = size.x;
		canvas.height = size.y;

		this.drawContent(canvas, coords);

		return canvas;
	},

	/**
	 * Tell L.GridLayer that the drawing of a specific (async) tile is ready
	 * @param coords
	 * @param error
	 * @param tile
	 * @private
	 */
	_asyncTileDone: function (coords, error, tile) {
		// tell GridLayer it is ready to readraw (see done-Argument in GridLayer.createTile)
		this._tileReady(coords, error, tile);
	},

	/**
	 * draw one tile
	 */
	drawContent: function (canvas, coords) {
		var ctx = canvas.getContext('2d');
		// disable smoothing since it causes ugly effects on tile's edges
		ctx.imageSmoothingEnabled = false;
		ctx.mozImageSmoothingEnabled = false;
		ctx.msImageSmoothingEnabled = false;

		var quadkey = quadkeys.tile2key(coords.x, coords.y, coords.z);

		var urlscheme = this._schemes.current;

		if (urlscheme === null) {
			return;
		}

		// don't draw anything for tiles that are hidden
		var zoom = coords.z;

		if (this.options.hideAfterZoom !== null && zoom > this.options.hideAfterZoom) {
			ctx.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
			return;
		}
		if (this.options.hideBeforeZoom !== null && zoom < this.options.hideBeforeZoom) {
			ctx.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
			return;
		}

		this.showLoading(canvas);

		// draw an 'X' if there's no tile for given coordinates
		var callback_error = function () {
			this.showMissing(canvas);
		}.bind(this);

		if (this.options.scaleTilesZoomGeq === null || coords.z < this.options.scaleTilesZoomGeq) {
			var callback = function (imgObj, img_was_in_cache) {
				if (this._schemes.current === urlscheme) {
					ctx.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
					ctx.drawImage(imgObj, 0, 0, this.options.tileSize, this.options.tileSize);
					this._asyncTileDone(coords, null, ctx.canvas);
				}
			}.bind(this);

			this._loadTile(quadkey, this._schemes.current, callback, callback_error);
		} else {
			// apply client side tile scaling...

			// how many zoom levels away from this.options.scaleTilesZoomGeq - 1?
			var no_zlevels = coords.z - this.options.scaleTilesZoomGeq + 1;
			// strip as many digits from quadkey
			var new_quadkey = quadkey.slice(0, -no_zlevels);
			var remaining_digits = quadkey.slice(-no_zlevels);

			var callback = function (image, cached) {
				if (this._schemes.current !== urlscheme) {
					return;
				}

				ctx.clearRect(0, 0, this.options.tileSize, this.options.tileSize);

				var sx = 0;
				var sy = 0;
				var sWidth = Math.round(this.options.tileSize / Math.pow(2, no_zlevels));
				var sHeight = sWidth;

				for (var i = 0; i < remaining_digits.length; i++) {
					var delta_px = Math.round(this.options.tileSize / Math.pow(2, i + 1));
					var digit = remaining_digits.charAt(i);

					// recall that digit denotes one of the four segments:
					//  ---------
					//  | 0 | 1 |
					//  ---------
					//  | 2 | 3 |
					//  ---------
					switch (digit) {
						case '1':
							sx = sx + delta_px;
							break;

						case '2':
							sy = sy + delta_px;
							break;

						case '3':
							sx = sx + delta_px;
							sy = sy + delta_px;
							break;
					}
				}

				ctx.drawImage(
					image,
					// x, y and width, height in source tile
					sx, sy,
					sWidth, sHeight,
					// x, y and width, height in destination tile
					0, 0,
					this.options.tileSize,
					this.options.tileSize
				);

				this._asyncTileDone(coords, null, ctx.canvas);
			}.bind(this);

			this._loadTile(new_quadkey, this._schemes.current, callback, callback_error);
		}
	},

	/**
	 * display hourglass on tile
	 */
	showLoading: function (canvas) {
		var ctx = canvas.getContext('2d');

		var x0 = 5;
		var y0 = 5;
		var height = 30;
		var width = 15;
		var margin = 4; // top and bottom margin
		var gap = 10; // 'straight line' on top and bottom
		var west = x0 + margin;
		var east = west + width;
		var north = y0;
		var south = north + height;

		ctx.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
		ctx.beginPath();

		ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';

		// draw top and bottom thick bars
		ctx.lineWidth = 4;
		ctx.moveTo(west - margin, north);
		ctx.lineTo(east + margin, north);
		ctx.moveTo(west - margin, south);
		ctx.lineTo(east + margin, south);
		ctx.stroke();

		// right part
		ctx.lineWidth = 2;
		ctx.moveTo(east, north);
		ctx.lineTo(east, north + gap);
		ctx.lineTo(east - width / 2 + 2, north + height / 2);
		ctx.lineTo(east, south - gap);
		ctx.lineTo(east, south);
		ctx.stroke();

		// left part
		ctx.moveTo(west, south);
		ctx.lineTo(west, south - gap);
		ctx.lineTo(west + width / 2 - 2, north + height / 2);
		ctx.lineTo(west, north + gap);
		ctx.lineTo(west, north);
		ctx.stroke();
	},

	/**
	 * Draw an 'X' on tile
	 */
	showMissing: function (canvas) {
		var ctx = canvas.getContext('2d');

		var width = 30;
		var x0 = this.options.tileSize / 2 - width / 2;
		var y0 = x0;
		var west = x0;
		var east = west + width;
		var north = y0;
		var south = north + width;

		ctx.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
		ctx.beginPath();

		ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';

		ctx.lineWidth = 12;
		ctx.moveTo(west, south);
		ctx.lineTo(east, north);
		ctx.moveTo(west, north);
		ctx.lineTo(east, south);
		ctx.stroke();

		L.DomUtil.addClass(canvas, 'leaflet-tile-error');
	},

	/**
	 * Print text on map tile
	 * @param {string} text text to be printed
	 * @param {obj} ctx Canvas 2d context
	 */
	printText: function (text, ctx) {
		var prevAlpha = ctx.globalAlpha;

		ctx.clearRect(0, 0, this.options.tileSize, this.options.tileSize);
		ctx.globalAlpha = 0.6;
		ctx.font = "700 16px 'adelle-sans', sans-serif";
		ctx.fillStyle = '#003453';
		ctx.textAlign = 'center';
		ctx.baseline = 'ideographic';
		ctx.fillText(text, this.options.tileSize / 2, this.options.tileSize / 2);
		ctx.globalAlpha = prevAlpha;
	},
});
